import { __ } from '@wordpress/i18n';
import { useState, useEffect, useCallback } from '@wordpress/element';
import SettingsHeader from "./Components/SettingsHeader";
import PromotionalSection from "./Components/PromotionalSection";
import Widgets from "./Components/Widgets";
import Extensions from "./Components/Extensions";
import WelcomeSection from './Components/WelcomeSection';
import FeatureRequestWidget from './Components/FeatureRequestWidget';
import UnlockProBanner from './Components/UnlockProBanner';
import AskForSupport from './Components/AskForSupport';
import SettingsTab from "./Components/SettingsTab";
import FeatureComparison from "./Components/FeatureComparison";
import CustomCodeTab from "./Components/CustomCodeTab";

const App = () => {
  const [activeItem, setActiveItem] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });
  const [widgetsData, setWidgetsData] = useState({ all: {}, enabled: [] });
  const [extensionsData, setExtensionsData] = useState({ all: {}, enabled: [] });
  const [loading, setLoading] = useState(false);
  const [extensionsLoading, setExtensionsLoading] = useState(false);
  const [enabledWidgets, setEnabledWidgets] = useState([]);
  const [enabledExtensions, setEnabledExtensions] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setLoading(true);
    setExtensionsLoading(true);

    const widgetsRequest = fetch(`${window.thhaAdminSettings.ajaxurl}?action=thha_get_widgets&nonce=${window.thhaAdminSettings.nonce}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setWidgetsData(data.data);
          setEnabledWidgets(data.data.enabled || []);
          setActiveCategory('all');
        }
      })
      .catch(error => {
        console.error("Fetch error:", error);
      });

    const extensionsRequest = fetch(`${window.thhaAdminSettings.ajaxurl}?action=thha_get_extensions&nonce=${window.thhaAdminSettings.nonce}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setExtensionsData(data.data);
          setEnabledExtensions(data.data.enabled || []);
        }
      })
      .catch(error => {
        console.error("Fetch extensions error:", error);
      });

    Promise.all([widgetsRequest, extensionsRequest]).finally(() => {
      setLoading(false);
      setExtensionsLoading(false);
    });
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeItem) {
      params.set('tab', activeItem);
      if (activeItem !== 'settings') {
        params.delete('sub_tab');
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [activeItem]);

  // Handle external redirect for unlock-pro tab
  useEffect(() => {
    if (activeItem === 'unlock-pro') {
      window.open(window.thhaAdminSettings.pricing, '_blank');
      setActiveItem('dashboard');
    }
  }, [activeItem]);

  // Handle external redirect for support tab
  useEffect(() => {
    if (activeItem === 'support') {
      const supportUrl = window.thhaAdminSettings.is_pro_active
        ? 'https://hugeaddons.com/support-request/'
        : 'https://wordpress.org/support/plugin/huge-addons-for-elementor/';
      window.open(supportUrl, '_blank');
      setActiveItem('dashboard');
    }
  }, [activeItem]);

  // Sync WordPress sidebar submenu active state with React activeItem tab
  useEffect(() => {
    const parentMenu = document.getElementById('toplevel_page_thha-addons-manager');
    if (!parentMenu) return;

    const subItems = parentMenu.querySelectorAll('.wp-submenu li');
    subItems.forEach(li => {
      li.classList.remove('current');
      const a = li.querySelector('a');
      if (a) {
        a.classList.remove('current');
        a.removeAttribute('aria-current');
      }
    });

    let targetLink = null;
    if (activeItem === 'dashboard') {
      subItems.forEach(li => {
        const a = li.querySelector('a');
        if (a && a.getAttribute('href')) {
          const href = a.getAttribute('href');
          if (href.indexOf('tab=') === -1) {
            targetLink = a;
          }
        }
      });
    } else {
      subItems.forEach(li => {
        const a = li.querySelector('a');
        if (a && a.getAttribute('href')) {
          const href = a.getAttribute('href');
          if (href.indexOf(`tab=${activeItem}`) !== -1) {
            targetLink = a;
          }
        }
      });
    }

    if (targetLink) {
      const li = targetLink.closest('li');
      if (li) {
        li.classList.add('current');
      }
      targetLink.classList.add('current');
      targetLink.setAttribute('aria-current', 'page');
    }
  }, [activeItem]);

  return (
    <div className="settings-wrapper">
      <SettingsHeader
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      {activeItem === 'dashboard' && (
        <>
          <PromotionalSection section1={WelcomeSection} section2={FeatureRequestWidget} />
          <Widgets
            numberOfCategories={2}
            perCategoryItem={12}
            isSearchEnable={false}
            setActiveItem={setActiveItem}
            widgetsData={widgetsData}
            setWidgetsData={setWidgetsData}
            enabledWidgets={enabledWidgets}
            setEnabledWidgets={setEnabledWidgets}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            loading={loading}
            setLoading={setLoading}
          />
          <PromotionalSection section1={UnlockProBanner} section2={AskForSupport} />
        </>
      )}

      {activeItem === 'widgets' && (
        <Widgets
          numberOfCategories={'all'}
          perCategoryItem={'all'}
          isSearchEnable={true}
          setActiveItem={setActiveItem}
          widgetsData={widgetsData}
          setWidgetsData={setWidgetsData}
          enabledWidgets={enabledWidgets}
          setEnabledWidgets={setEnabledWidgets}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          loading={loading}
          setLoading={setLoading}
        />
      )}

      {activeItem === 'extensions' && (
        <Extensions
          extensionsData={extensionsData}
          enabledExtensions={enabledExtensions}
          setEnabledExtensions={setEnabledExtensions}
          loading={loading || extensionsLoading}
        />
      )}

      {activeItem === 'settings' && <SettingsTab />}

      {activeItem === 'custom-code' && window.thhaAdminSettings.enable_custom_code === '1' && <CustomCodeTab />}

      {activeItem === 'free-vs-pro' && <FeatureComparison />}

      {/* The 'unlock-pro' link is now handled directly by the MenuItem component */}
    </div>
  );
};

export default App;
